import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text, Localizer } from 'preact-i18n';
import util from 'preact-util';

import Input from '../../components/form/input';
import { compareTextTemplates } from '../../lib/textTemplateSort';
import {
    fillMergeFields, countSmsSegments, isActiveStatus, defaultSelection, uniqueRecipientCount,
} from '../../lib/smsBulk';
import {
    SmsBulkSenderLine, SmsBulkTemplates, SmsBulkTestButton, SmsBulkProgress, DEFAULT_SENDER,
} from './smsBulkParts';

const POLL_MS = 3000;
const MERGE_FIELDS_HINT = '{{fornavn}}, {{etternavn}}, {{tid}}, {{dyr}}';

function formatDay(calendarDate) {
    const date = new Date(`${calendarDate}T12:00:00`);
    if (Number.isNaN(date.getTime())) {
        return calendarDate || '';
    }
    return util.formatDate(date, {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', locale: 'nb-NO',
    });
}

/*
 * Kalender-SMS: one SMS to every owner booked on one day. Opened from
 * components/calendar/new.js with { calendarDate: 'YYYY-MM-DD', calendarIds }.
 * The backend lists the day's appointments (preview) and resolves the ticked
 * ones to recipients (create); the Utsendelse cron worker sends. This drawer
 * only lists, selects, composes, polls and cancels.
 */
@observer
class DyrejournalSendCalendarDaySms extends Component {
    constructor(props) {
        super(props);
        this.state = {
            step: 'compose',
            candidates: [],
            selected: [],
            loading: true,
            textTemplates: [],
            showTemplates: false,
            confirmed: false,
            inProgress: false,
            testInProgress: false,
            testSent: false,
            error: '',
            job: null,
        };
        this.pollTimer = null;
    }

    componentDidMount() {
        const { smsBulkStore } = this.props.stores;
        smsBulkStore.resetNewSmsBulk();
        this.loadCandidates();
        this.loadTemplates();
    }

    componentWillUnmount() {
        this.stopPolling();
    }

    loadCandidates = async () => {
        const { calendarEventStore } = this.props.stores;
        const { calendarDate, calendarIds = [] } = this.props;
        const response = await calendarEventStore.bulkSmsPreview({ calendarDate, calendarIds });
        if (response && response.status === 200 && response.data) {
            const candidates = Array.isArray(response.data.candidates) ? response.data.candidates : [];
            this.setState({ candidates, selected: defaultSelection(candidates), loading: false });
        } else {
            this.setState({ loading: false, error: 'Kunne ikke hente avtalene. Prøv igjen.' });
        }
    }

    loadTemplates = async () => {
        const { textTemplateStore } = this.props.stores;
        const tpls = await textTemplateStore.load({ query: { section: 'sendSms' }, skipUpdate: 1 });
        this.setState({ textTemplates: Array.isArray(tpls) ? [...tpls].sort(compareTextTemplates) : [] });
    }

    toggleTemplates = () => {
        this.setState(prev => ({ showTemplates: !prev.showTemplates }));
    }

    applyTemplate = (e) => {
        const { smsBulkStore } = this.props.stores;
        const { body } = e.currentTarget.dataset;
        smsBulkStore.updateObjectKeyValue('newSmsBulk', 'body', body || '');
        this.setState({ showTemplates: false });
    }

    toggleOne = (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10);
        const { checked } = e.currentTarget;
        this.setState(prev => {
            const without = prev.selected.filter(x => x !== id);
            return { selected: checked ? [...without, id] : without };
        });
    }

    selectAll = () => {
        this.setState(prev => ({ selected: defaultSelection(prev.candidates) }));
    }

    selectNone = () => {
        this.setState({ selected: [] });
    }

    toggleConfirmed = (e) => {
        this.setState({ confirmed: !!e.target.checked });
    }

    firstSelected = () => {
        const { candidates, selected } = this.state;
        const wanted = new Set(selected);
        return candidates.find(c => c.ok && wanted.has(c.eventId)) || null;
    }

    sendTest = async () => {
        const { appState, userStore, smsBulkStore } = this.props.stores;
        const { user = {}, customer = {} } = userStore;
        const { newSmsBulk = {} } = smsBulkStore;
        if (!newSmsBulk.body || !user.cellphone) {
            return;
        }
        const sample = this.firstSelected();
        const me = {
            firstname: user.firstname,
            lastname: user.lastname,
            mergeFields: {
                tid: sample ? util.isoTime(sample.start) : '',
                dyr: sample ? sample.animalName : '',
            },
        };
        this.setState({ testInProgress: true, testSent: false });
        const response = await appState.sendSms({
            from: customer.smsSender || DEFAULT_SENDER,
            to: [user.cellphone],
            body: fillMergeFields(newSmsBulk.body, me),
        });
        this.setState({ testInProgress: false, testSent: !!(response && response.status === 200) });
    }

    send = async () => {
        const { calendarEventStore, smsBulkStore } = this.props.stores;
        const { newSmsBulk = {} } = smsBulkStore;
        const { calendarDate, calendarIds = [] } = this.props;
        const { selected } = this.state;
        this.setState({ inProgress: true, error: '' });
        const response = await calendarEventStore.bulkSmsCreate({
            calendarDate,
            calendarIds,
            eventIds: selected,
            body: newSmsBulk.body,
        });
        if (response && response.status === 201) {
            const job = Array.isArray(response.data) ? response.data[0] : response.data;
            this.setState({ step: 'sending', job, inProgress: false });
            this.startPolling(job.id);
            return;
        }
        let message = 'Kunne ikke starte utsendelsen. Prøv igjen.';
        if (response && response.status === 409) {
            message = 'En utsendelse pågår allerede. Vent til den er ferdig.';
        } else if (response && response.message) {
            message = response.message;
        }
        this.setState({ inProgress: false, error: message });
    }

    startPolling = (id) => {
        this.stopPolling();
        this.pollTimer = setInterval(() => this.poll(id), POLL_MS);
    }

    stopPolling = () => {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
        }
    }

    poll = async (id) => {
        const { smsBulkStore } = this.props.stores;
        const job = await smsBulkStore.loadOne(id);
        if (!job) {
            return;
        }
        if (isActiveStatus(job.status)) {
            this.setState({ job });
        } else {
            this.stopPolling();
            this.setState({ job, step: 'done' });
        }
    }

    cancel = async () => {
        const { smsBulkStore } = this.props.stores;
        const { job } = this.state;
        if (!job) {
            return;
        }
        const response = await smsBulkStore.cancel(job.id);
        if (response && response.status === 200) {
            const updated = Array.isArray(response.data) ? response.data[0] : response.data;
            this.setState({ job: updated || { ...job, status: 'cancelled' } });
        }
    }

    close = () => {
        const { appState } = this.props.stores;
        this.stopPolling();
        appState.toggleDrawer();
    }

    renderCandidates() {
        const {
            candidates, selected, loading, error,
        } = this.state;
        if (loading) {
            return (
                <div class='text-body-secondary mb-3'>
                    <i class='fa-duotone fa-spinner fa-spin me-1' />
                    <Text id='dyrejournal.calendar-sms-loading'>Loading appointments...</Text>
                </div>
            );
        }
        if (error && candidates.length === 0) {
            return (
                <div class='alert alert-danger py-2 mb-3'>{error}</div>
            );
        }
        if (candidates.length === 0) {
            return (
                <div class='alert alert-info py-2 mb-3'>
                    <Text id='dyrejournal.calendar-sms-empty'>No appointments this day.</Text>
                </div>
            );
        }
        const wanted = new Set(selected);
        const recipients = uniqueRecipientCount(candidates, selected);
        return (
            <div class='mb-3'>
                <div class='d-flex justify-content-between small mb-1'>
                    <span>
                        <button type='button' class='btn btn-link btn-sm p-0 me-3' onClick={this.selectAll}>
                            <Text id='dyrejournal.calendar-sms-select-all'>Select all</Text>
                        </button>
                        <button type='button' class='btn btn-link btn-sm p-0' onClick={this.selectNone}>
                            <Text id='dyrejournal.calendar-sms-select-none'>Select none</Text>
                        </button>
                    </span>
                    <span class='text-body-secondary'>
                        <Text id='dyrejournal.calendar-sms-selected' fields={{ selected: selected.length, recipients }}>
                            {selected.length} appointments selected, {recipients} recipients
                        </Text>
                    </span>
                </div>
                <div class='list-group' style='max-height: 40vh; overflow-y: auto;'>
                    {candidates.map(c => (
                        <label
                            key={c.eventId}
                            class={`list-group-item d-flex align-items-center gap-2 py-1 ${c.ok ? '' : 'text-body-secondary'}`}
                            style='cursor: pointer;'
                        >
                            <input
                                class='form-check-input m-0 flex-shrink-0'
                                type='checkbox'
                                disabled={!c.ok}
                                checked={wanted.has(c.eventId)}
                                data-id={c.eventId}
                                onChange={this.toggleOne}
                            />
                            <span class='text-nowrap font-monospace'>{util.isoTime(c.start)}</span>
                            <span class='flex-fill text-truncate'>
                                {c.animalName && <strong class='me-1'>{c.animalName}</strong>}
                                {c.visitorName || c.title}
                            </span>
                            <span class='text-nowrap small'>
                                {c.ok && c.cellphone}
                                {c.reason === 'noOwner' && <Text id='dyrejournal.calendar-sms-no-owner'>no owner</Text>}
                                {c.reason === 'noMobile' && <Text id='dyrejournal.calendar-sms-no-mobile'>no mobile number</Text>}
                            </span>
                        </label>
                    ))}
                </div>
            </div>
        );
    }

    renderCompose() {
        const { smsBulkStore, userStore } = this.props.stores;
        const { newSmsBulk = {} } = smsBulkStore;
        const { user = {}, customer = {} } = userStore;
        const {
            candidates, selected, textTemplates, showTemplates, confirmed, inProgress, testInProgress, testSent, error,
        } = this.state;
        const body = newSmsBulk.body || '';
        const { chars, segments } = countSmsSegments(body);
        const recipients = uniqueRecipientCount(candidates, selected);
        const canSend = recipients > 0 && body.trim().length > 0 && confirmed && !inProgress;

        return (<>
            {this.renderCandidates()}

            <SmsBulkSenderLine customer={customer} isEmail={false} />
            <SmsBulkTemplates
                textTemplates={textTemplates}
                showTemplates={showTemplates}
                onToggle={this.toggleTemplates}
                onApply={this.applyTemplate}
            />

            <Localizer>
                <Input
                    classNames='form-control rounded-lg'
                    title={<Text id='input.sms-body'>Sms content</Text>}
                    placeholder={<Text id='input.sms-body'>Sms content</Text>}
                    help={<Text id='input.sms-body-help'>Your sms content. Do NOT include links! SMS providers will stop the SMS from beeing sent.</Text>}
                    stores={this.props.stores}
                    holdingStore={smsBulkStore}
                    object={newSmsBulk}
                    objectName={'newSmsBulk'}
                    type={'textarea'}
                    field={'body'}
                    value={body}
                    extraDataset={{
                        'data-isnew': true,
                    }}
                />
            </Localizer>
            <div class='d-flex justify-content-between px-2 small text-body-secondary'>
                <span>
                    <Text id='dyrejournal.calendar-sms-merge-hint'>Merge fields:</Text> {MERGE_FIELDS_HINT}
                </span>
                <span>
                    <Text id='dyrejournal.sms-bulk-segments' fields={{ chars, segments }}>{chars} characters, approx. {segments} SMS per recipient</Text>
                </span>
            </div>

            <SmsBulkTestButton
                user={user}
                isEmail={false}
                body={body}
                testInProgress={testInProgress}
                testSent={testSent}
                onSend={this.sendTest}
            />

            <div class='form-check mt-3'>
                <input class='form-check-input' type='checkbox' id='calendarDaySmsConfirm' checked={confirmed} onChange={this.toggleConfirmed} />
                <label class='form-check-label' for='calendarDaySmsConfirm'>
                    <Text id='dyrejournal.sms-bulk-confirm' fields={{ count: recipients }}>I understand this sends {recipients} SMS, invoiced to the clinic</Text>
                </label>
            </div>

            {error && candidates.length > 0 && <div class='alert alert-danger mt-3 py-2'>{error}</div>}

            <div class='d-flex justify-content-end gap-2 mt-3'>
                <button type='button' class='btn btn-outline-secondary rounded-pill' onClick={this.close}>
                    <Text id='dyrejournal.sms-bulk-cancel-button'>Cancel</Text>
                </button>
                <button type='button' class='btn btn-primary rounded-pill' disabled={!canSend} onClick={this.send}>
                    <i class={`fa-duotone ${inProgress ? 'fa-spinner fa-spin' : 'fa-comment-sms'} me-1`} />
                    <Text id='dyrejournal.sms-bulk-send' fields={{ count: recipients }}>Send {recipients} SMS</Text>
                </button>
            </div>
        </>);
    }

    render() {
        const { step, job } = this.state;
        const { calendarDate } = this.props;
        return (
            <div class='w-100 h-100 d-flex justify-content-center'>
                <div class='w-100 px-3' style='max-width: 720px;'>
                    <h5 class='mt-3 mb-2'>
                        <i class='fa-duotone fa-comment-sms me-2' />
                        <Text id='dyrejournal.calendar-sms-title' fields={{ date: formatDay(calendarDate) }}>
                            Send SMS to the appointments on {formatDay(calendarDate)}
                        </Text>
                    </h5>
                    {step === 'compose'
                        ? this.renderCompose()
                        : <SmsBulkProgress job={job} onCancel={this.cancel} onClose={this.close} />}
                </div>
            </div>
        );
    }
}

export default DyrejournalSendCalendarDaySms;
